import VERTC, {
  MirrorType,
  StreamIndex,
  IRTCEngine,
  RoomProfileType,
  onUserJoinedEvent,
  onUserLeaveEvent,
  MediaType,
  LocalStreamStats,
  RemoteStreamStats,
  StreamRemoveReason,
  LocalAudioPropertiesInfo,
  RemoteAudioPropertiesInfo,
  AudioProfileType,
  DeviceInfo,
  AutoPlayFailedEvent,
  PlayerEvent,
  NetworkQuality,
  VideoRenderMode,
  ScreenEncoderConfig,
} from '@volcengine/rtc';
import RTCAIAnsExtension from '@volcengine/rtc/extension-ainr';
import { Message } from '@arco-design/web-react';
import openAPIs from '@/api/apiTTS/api';
import aigcConfig from '@/configTTS';
import Utils from '@/utils/utilsTTS/utils';
import { COMMAND, INTERRUPT_PRIORITY } from '@/utils/utilsTTS/handler';

export interface IEventListener {
  handleError: (e: { errorCode: any }) => void;
  handleUserJoin: (e: onUserJoinedEvent) => void;
  handleUserLeave: (e: onUserLeaveEvent) => void;
  handleTrackEnded: (e: { kind: string; isScreen: boolean }) => void;
  handleUserPublishStream: (e: { userId: string; mediaType: MediaType }) => void;
  handleUserUnpublishStream: (e: {
    userId: string;
    mediaType: MediaType;
    reason: StreamRemoveReason;
  }) => void;
  handleRemoteStreamStats: (e: RemoteStreamStats) => void;
  handleLocalStreamStats: (e: LocalStreamStats) => void;
  handleLocalAudioPropertiesReport: (e: LocalAudioPropertiesInfo[]) => void;
  handleRemoteAudioPropertiesReport: (e: RemoteAudioPropertiesInfo[]) => void;
  handleAudioDeviceStateChanged: (e: DeviceInfo) => void;
  handleAutoPlayFail: (e: AutoPlayFailedEvent) => void;
  handlePlayerEvent: (e: PlayerEvent) => void;
  handleUserStartAudioCapture: (e: { userId: string }) => void;
  handleUserStopAudioCapture: (e: { userId: string }) => void;
  handleRoomBinaryMessageReceived: (e: { userId: string; message: ArrayBuffer }) => void;
  handleNetworkQuality: (
    uplinkNetworkQuality: NetworkQuality,
    downlinkNetworkQuality: NetworkQuality
  ) => void;
}

interface EngineOptions {
  appId: string;
  uid: string;
  roomId: string;
}

export interface BasicBody {
  room_id: string;
  user_id: string;
  login_token: string | null;
}

/**
 * @brief RTC Core Client
 * @notes Refer to official website documentation to get more information about the API.
 */
export class RTCClient {
  engine!: IRTCEngine;

  config!: EngineOptions;

  basicInfo!: BasicBody;

  private _audioCaptureDevice?: string;

  private _videoCaptureDevice?: string;

  audioBotEnabled = false;

  audioBotStartTime = 0;

  isScreenSharingActive = false;

  createEngine = async (props: EngineOptions) => {
    this.config = props;
    this.basicInfo = {
      room_id: props.roomId,
      user_id: props.uid,
      login_token: aigcConfig.BaseConfig.Token,
    };

    this.engine = VERTC.createEngine(this.config.appId);
    try {
      const AIAnsExtension = new RTCAIAnsExtension();
      await this.engine.registerExtension(AIAnsExtension);
      AIAnsExtension.enable();
    } catch (error) {
      console.warn(
        `当前环境不支持 AI 降噪, 此错误可忽略, 不影响实际使用, e: ${(error as any).message}`
      );
    }
  };

  addEventListeners = ({
    handleError,
    handleUserJoin,
    handleUserLeave,
    handleTrackEnded,
    handleUserPublishStream,
    handleUserUnpublishStream,
    handleRemoteStreamStats,
    handleLocalStreamStats,
    handleLocalAudioPropertiesReport,
    handleRemoteAudioPropertiesReport,
    handleAudioDeviceStateChanged,
    handleAutoPlayFail,
    handlePlayerEvent,
    handleUserStartAudioCapture,
    handleUserStopAudioCapture,
    handleRoomBinaryMessageReceived,
    handleNetworkQuality,
  }: IEventListener) => {
    this.engine.on(VERTC.events.onError, handleError);
    this.engine.on(VERTC.events.onUserJoined, handleUserJoin);
    this.engine.on(VERTC.events.onUserLeave, handleUserLeave);
    this.engine.on(VERTC.events.onTrackEnded, handleTrackEnded);
    this.engine.on(VERTC.events.onUserPublishStream, handleUserPublishStream);
    this.engine.on(VERTC.events.onUserUnpublishStream, handleUserUnpublishStream);
    this.engine.on(VERTC.events.onRemoteStreamStats, handleRemoteStreamStats);
    this.engine.on(VERTC.events.onLocalStreamStats, handleLocalStreamStats);
    this.engine.on(VERTC.events.onAudioDeviceStateChanged, handleAudioDeviceStateChanged);
    this.engine.on(VERTC.events.onLocalAudioPropertiesReport, handleLocalAudioPropertiesReport);
    this.engine.on(VERTC.events.onRemoteAudioPropertiesReport, handleRemoteAudioPropertiesReport);
    this.engine.on(VERTC.events.onAutoplayFailed, handleAutoPlayFail);
    this.engine.on(VERTC.events.onPlayerEvent, handlePlayerEvent);
    this.engine.on(VERTC.events.onUserStartAudioCapture, handleUserStartAudioCapture);
    this.engine.on(VERTC.events.onUserStopAudioCapture, handleUserStopAudioCapture);
    this.engine.on(VERTC.events.onRoomBinaryMessageReceived, handleRoomBinaryMessageReceived);
    this.engine.on(VERTC.events.onNetworkQuality, handleNetworkQuality);
  };

  joinRoom = (token: string | null, username: string): Promise<void> => {
    this.engine.enableAudioPropertiesReport({ interval: 1000 });
    return this.engine.joinRoom(
      token,
      `${this.config.roomId!}`,
      {
        userId: this.config.uid!,
        extraInfo: JSON.stringify({
          call_scene: 'RTC-AIGC',
          user_name: username,
          user_id: this.config.uid,
        }),
      },
      {
        isAutoPublish: true,
        isAutoSubscribeAudio: true,
        roomProfileType: RoomProfileType.chat,
      }
    );
  };

  leaveRoom = () => {
    this.stopAudioBot();
    this.audioBotEnabled = false;
    this.engine.leaveRoom();
    VERTC.destroyEngine(this.engine);
    this._audioCaptureDevice = undefined;
  };

  checkPermission(): Promise<{
    video: boolean;
    audio: boolean;
  }> {
    return VERTC.enableDevices({
      video: false,
      audio: true,
    });
  }

  /**
   * @brief get the devices
   * @returns
   */
  async getDevices(props?: { video?: boolean; audio?: boolean }): Promise<{
    audioInputs: MediaDeviceInfo[];
    audioOutputs: MediaDeviceInfo[];
    videoInputs: MediaDeviceInfo[];
  }> {
    const { video = false, audio = true } = props || {};
    let audioInputs: MediaDeviceInfo[] = [];
    let audioOutputs: MediaDeviceInfo[] = [];
    let videoInputs: MediaDeviceInfo[] = [];
    const { video: hasVideoPermission, audio: hasAudioPermission } = await VERTC.enableDevices({
      video,
      audio,
    });
    if (audio) {
      const inputs = await VERTC.enumerateAudioCaptureDevices();
      const outputs = await VERTC.enumerateAudioPlaybackDevices();
      audioInputs = inputs.filter((i) => i.deviceId && i.kind === 'audioinput');
      audioOutputs = outputs.filter((i) => i.deviceId && i.kind === 'audiooutput');
      this._audioCaptureDevice = audioInputs.filter((i) => i.deviceId)?.[0]?.deviceId;
      if (hasAudioPermission) {
        if (!audioInputs?.length) {
          Message.error('无麦克风设备, 请先确认设备情况。');
        }
        if (!audioOutputs?.length) {
          Message.error('无扬声器设备, 请先确认设备情况。');
        }
      } else {
        Message.error('暂无麦克风设备权限, 请先确认设备权限授予情况。');
      }
    }
    if (video) {
      videoInputs = await VERTC.enumerateVideoCaptureDevices();
      videoInputs = videoInputs.filter((i) => i.deviceId && i.kind === 'videoinput');
      this._videoCaptureDevice = videoInputs?.[0]?.deviceId;
      if (hasVideoPermission) {
        if (!videoInputs?.length) {
          Message.error('无摄像头设备, 请先确认设备情况。');
        }
      } else {
        Message.error('暂无摄像头设备权限, 请先确认设备权限授予情况。');
      }
    }

    return {
      audioInputs,
      audioOutputs,
      videoInputs,
    };
  }

  startVideoCapture = async (camera?: string) => {
    await this.engine.startVideoCapture(camera || this._videoCaptureDevice);
  };

  stopVideoCapture = async () => {
    this.engine.setLocalVideoMirrorType(MirrorType.MIRROR_TYPE_RENDER);
    await this.engine.stopVideoCapture();
  };

  startScreenCapture = async (enableAudio = false) => {
    await this.engine.startScreenCapture({
      enableAudio,
    });
  };

  stopScreenCapture = async () => {
    await this.engine.stopScreenCapture();
  };

  /**
   * @brief 枚举可用的媒体输入和输出设备
   * @returns 媒体设备列表
   */
  enumerateDevices = async (): Promise<MediaDeviceInfo[]> => {
    try {
      // 确保已经获取了设备权限
      await VERTC.enableDevices({
        video: true,
        audio: true,
      });
      console.log('获取设备列表...',Audio);
      // 枚举所有类型的设备
      const audioInputs = await VERTC.enumerateAudioCaptureDevices();
      const audioOutputs = await VERTC.enumerateAudioPlaybackDevices();
      const videoInputs = await VERTC.enumerateVideoCaptureDevices();
      // 过滤并合并所有有效设备
      const validAudioInputs = audioInputs.filter(
        (device) => device.deviceId && device.kind === 'audioinput'
      );
      const validAudioOutputs = audioOutputs.filter(
        (device) => device.deviceId && device.kind === 'audiooutput'
      );
      const validVideoInputs = videoInputs.filter(
        (device) => device.deviceId && device.kind === 'videoinput'
      );

      // 合并所有设备并返回
      return [...validAudioInputs, ...validAudioOutputs, ...validVideoInputs];
    } catch (error) {
      console.error('enumerateDevices failed:', error);
      throw error;
    }
  };
  /**
   * @brief 开启内部音频采集
   * @param deviceId 设备 ID
   * @returns 实际生效的音频采集参数
   */
  startAudioCapture = async (deviceId?: string): Promise<MediaTrackSettings> => {

    try {
      // 调用引擎开启音频采集
      const settings = await this.engine.startAudioCapture(deviceId || this._audioCaptureDevice);

      // 更新当前使用的音频采集设备
      if (deviceId) {
        this._audioCaptureDevice = deviceId;
      }
      console.log('startAudioCapture', deviceId || this._audioCaptureDevice);
      console.log('startAudioCapture', settings);
      // 返回实际生效的音频采集参数
      return settings;
    } catch (error: any) {
      // 根据错误情况抛出对应的错误码
      if (error.name === 'NotAllowedError' || error.name === 'NotFoundError') {
        throw new Error('GET_AUDIO_TRACK_FAILED');
      } else if (error.message && error.message.includes('repeat') || error.message.includes('Repeat')) {
        // 对于重复采集错误，我们将其视为成功，因为目标已经是激活状态
        // 这样可以避免在切换麦克风时出现不必要的错误
        throw new Error('REPEAT_CAPTURE');
      } else {
        throw new Error('STREAM_TYPE_NOT_MATCH');
      }
    }
  };
  stopAudioCapture = async () => {
    await this.engine.stopAudioCapture();
  };

  publishStream = (mediaType: MediaType) => {
    this.engine.publishStream(mediaType);
  };

  unpublishStream = (mediaType: MediaType) => {
    this.engine.unpublishStream(mediaType);
  };

  publishScreenStream = async (mediaType: MediaType) => {
    await this.engine.publishScreen(mediaType);
  };

  unpublishScreenStream = async (mediaType: MediaType) => {
    await this.engine.unpublishScreen(mediaType);
  };

  setScreenEncoderConfig = async (description: ScreenEncoderConfig) => {
    await this.engine.setScreenEncoderConfig(description);
  };

  /**
   * @brief 设置业务标识参数
   * @param businessId
   */
  setBusinessId = (businessId: string) => {
    this.engine.setBusinessId(businessId);
  };

  setAudioVolume = (volume: number) => {
    this.engine.setCaptureVolume(StreamIndex.STREAM_INDEX_MAIN, volume);
    this.engine.setCaptureVolume(StreamIndex.STREAM_INDEX_SCREEN, volume);
  };

  /**
   * @brief 设置音质档位
   */
  setAudioProfile = (profile: AudioProfileType) => {
    this.engine.setAudioProfile(profile);
  };

  /**
   * @brief 切换设备
   */
  switchDevice = (deviceType: MediaType, deviceId: string) => {
    if (deviceType === MediaType.AUDIO) {
      this._audioCaptureDevice = deviceId;
      this.engine.setAudioCaptureDevice(deviceId);
    }
    if (deviceType === MediaType.VIDEO) {
      this._videoCaptureDevice = deviceId;
      this.engine.setVideoCaptureDevice(deviceId);
    }
    if (deviceType === MediaType.AUDIO_AND_VIDEO) {
      this._audioCaptureDevice = deviceId;
      this._videoCaptureDevice = deviceId;
      this.engine.setVideoCaptureDevice(deviceId);
      this.engine.setAudioCaptureDevice(deviceId);
    }
  };

  setLocalVideoMirrorType = (type: MirrorType) => {
    return this.engine.setLocalVideoMirrorType(type);
  };

  setLocalVideoPlayer = (
    userId: string,
    renderDom?: string | HTMLElement,
    isScreenShare = false
  ) => {
    // 检查是否为移动设备（H5）
    const isH5 = /Mobi|Android|iPhone|iPad|Windows Phone/i.test(window.navigator.userAgent);

    return this.engine.setLocalVideoPlayer(
      isScreenShare ? StreamIndex.STREAM_INDEX_SCREEN : StreamIndex.STREAM_INDEX_MAIN,
      {
        renderDom,
        userId,
        renderMode: isH5 ? VideoRenderMode.RENDER_MODE_FIT : VideoRenderMode.RENDER_MODE_FILL,
      }
    );
  };

  /**
   * @brief 启用 AIGC
   */
  startAudioBot = async () => {
    const roomId = this.basicInfo.room_id;
    const userId = this.basicInfo.user_id;
    if (this.audioBotEnabled) {
      await this.stopAudioBot();
    }
    const agentConfig = aigcConfig.aigcConfig.AgentConfig;

    const options = {
      AppId: aigcConfig.BaseConfig.AppId,
      BusinessId: aigcConfig.BaseConfig.BusinessId,
      RoomId: roomId,
      TaskId: userId,
      AgentConfig: {
        ...agentConfig,
        TargetUserId: [userId],
      },
      Config: aigcConfig.aigcConfig.Config,
    };
    await openAPIs.StartVoiceChat(options);
    this.audioBotEnabled = true;
    this.audioBotStartTime = Date.now();
    Utils.setSessionInfo({ audioBotEnabled: 'enable' });
  };

  /**
   * @brief 关闭 AIGC
   */
  stopAudioBot = async () => {
    const roomId = this.basicInfo.room_id;
    const userId = this.basicInfo.user_id;
    if (this.audioBotEnabled || sessionStorage.getItem('audioBotEnabled')) {
      await openAPIs.StopVoiceChat({
        AppId: aigcConfig.BaseConfig.AppId,
        BusinessId: aigcConfig.BaseConfig.BusinessId,
        RoomId: roomId,
        TaskId: userId,
      });
      this.audioBotStartTime = 0;
      sessionStorage.removeItem('audioBotEnabled');
    }
    this.audioBotEnabled = false;
  };

  /**
   * @brief 命令 AIGC
   */
  commandAudioBot = (command: COMMAND, interruptMode = INTERRUPT_PRIORITY.NONE, message = '') => {
    if (this.audioBotEnabled) {
      this.engine.sendUserBinaryMessage(
        aigcConfig.BotName,
        Utils.string2tlv(
          JSON.stringify({
            Command: command,
            InterruptMode: interruptMode,
            Message: message,
          }),
          'ctrl'
        )
      );
      return;
    }
    console.warn('Interrupt failed, bot not enabled.');
  };

  /**
   * @brief 更新 AIGC 配置
   */
  updateAudioBot = async () => {
    if (this.audioBotEnabled) {
      await this.stopAudioBot();
      await this.startAudioBot();
    } else {
      await this.stopAudioBot();
      await this.startAudioBot();
    }
  };

  /**
   * @brief 获取当前 AI 是否启用
   */
  getAudioBotEnabled = () => {
    return this.audioBotEnabled;
  };

  /**
   * @brief 获取本地流的 MediaStreamTrack
   * @param streamIndex 流索引 (主流或屏幕流)
   * @param type 媒体类型 ('video' 或 'audio')
   * @returns MediaStreamTrack 或 undefined
   */
  getLocalStreamTrack = (streamIndex: StreamIndex, type: 'video' | 'audio'): MediaStreamTrack | undefined => {
    try {
      return this.engine.getLocalStreamTrack(streamIndex, type);
    } catch (error) {
      console.warn('获取本地流轨道失败:', error);
      return undefined;
    }
  };
  /**
 * @brief 将ImageData转换为Base64格式
 * @param imageData 图像数据
 * @returns Base64编码的图像字符串
 */
  imageDataToBase64 = (imageData: ImageData): string => {
    // 检查imageData是否有效
    if (!imageData || !imageData.width || !imageData.height) {
      throw new Error('无效的图像数据');
    }

    console.log('imageDataToBase64', imageData);
    // 创建Canvas元素并获取上下文
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // 设置canvas尺寸
    canvas.width = imageData.width;
    canvas.height = imageData.height;

    // 检查context是否存在
    if (context) {
      // 将图像数据绘制到canvas上
      context.putImageData(imageData, 0, 0);
    } else {
      throw new Error('无法获取canvas上下文');
    }

    // 转换为Base64格式
    return canvas.toDataURL('image/png');
  };
  /**
   * @brief 将ImageData保存为PNG文件
   * @param imageData 图像数据
   * @param filename 文件名，默认为"截图"
   * @returns Promise<void>
   */
  saveImageDataAsPNG = async (imageData: ImageData, filename: string = '截图'): Promise<void> => {
    try {
      // 创建Canvas元素并获取上下文
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      // 设置canvas尺寸
      canvas.width = imageData.width;
      canvas.height = imageData.height;

      // 检查context是否存在
      if (context) {
        // 将图像数据绘制到canvas上
        context.putImageData(imageData, 0, 0);
      } else {
        throw new Error('无法获取canvas上下文');
      }

      // 转换为Base64格式
      const url = canvas.toDataURL('image/png');

      // 创建下载链接
      const a = document.createElement('a');
      a.href = url;
      a.download = filename + '-' + Date.now() + '.png';
      a.click();

      // 清理创建的元素
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('保存图片失败:', error);
    }
  };
  // /**
  //   * @brief 获取本地视频流截图
  //   * @param streamIndex 视频流索引，默认为主流
  //   * @returns Promise<any> 返回截图的ImageData对象
  //   */
  takeLocalSnapshot = async (streamIndex: StreamIndex = StreamIndex.STREAM_INDEX_MAIN): Promise<any> => {
    try {
      // 检查本地视频轨道是否已存在
      const localVideoTrack = this.engine.getLocalStreamTrack(streamIndex, 'video');
      if (!localVideoTrack) {
        throw new Error('指定的视频流不存在，请确保指定的流已发布');
      }

      // 检查视频轨道是否有效且未结束
      if (localVideoTrack.readyState !== 'live') {
        throw new Error('视频轨道未处于活动状态');
      }
      return await this.engine.takeLocalSnapshot(streamIndex);
    } catch (error) {
      console.warn('获取本地视频轨道失败:', error);
      return;
    }
  };
  /**
   * @brief 获取远程视频流截图
   * @param id 用户 ID/ WTN 流 ID
   * @param streamIndex 视频流索引，默认为主流
   * @returns Promise<any> 返回截图的ImageData对象
   */
  takeRemoteSnapshot = async (id: string, streamIndex: StreamIndex = StreamIndex.STREAM_INDEX_MAIN): Promise<any> => {
    try {
      // 确保streamIndex是有效的值
      const validStreamIndex = streamIndex === StreamIndex.STREAM_INDEX_MAIN || streamIndex === StreamIndex.STREAM_INDEX_SCREEN
        ? streamIndex
        : StreamIndex.STREAM_INDEX_MAIN;

      // 检查远程视频轨道是否已存在
      const remoteVideoTrack = this.engine.getRemoteStreamTrack(id, validStreamIndex, 'video');
      if (!remoteVideoTrack) {
        throw new Error('指定的远程视频流不存在，请确保已订阅指定的流');
      }

      // 检查视频轨道是否有效且未结束
      if (remoteVideoTrack.readyState !== 'live') {
        throw new Error('远程视频轨道未处于活动状态');
      }
      return await this.engine.takeRemoteSnapshot(id, validStreamIndex);
    } catch (error) {
      console.warn('获取远程视频轨道失败:', error);
      return null;
    }
  };
  /**
 * @brief 获取远程视频流截图并转换为Base64
 * @param id 用户 ID/ WTN 流 ID
 * @param streamIndex 视频流索引，默认为主流
 * @returns Promise<string | null> 返回Base64编码的图像字符串或null
 */
  getRemoteSnapshotAsBase64 = async (id: string, streamIndex: StreamIndex = StreamIndex.STREAM_INDEX_MAIN): Promise<string | null> => {
    try {
      // 获取远程截图
      const imageData = await this.takeRemoteSnapshot(id, streamIndex);
      if (!imageData) {
        return null;
      }
      return this.imageDataToBase64(imageData);
    } catch (error) {
      console.error('远程截图失败:', error);
      return null;
    }
  };
  /**
 * @brief 检查当前是否处于语音通话会话中
 * @returns boolean 返回是否处于语音通话会话中
 */
  isVoiceSessionActive = (): boolean => {
    // 检查音频机器人是否启用且开始时间存在
    return this.audioBotEnabled && this.audioBotStartTime > 0;
  };

  /**
   * @brief 获取当前语音通话会话的开始时间
   * @returns number 返回语音通话会话的开始时间戳
   */
  getVoiceSessionStartTime = (): number => {
    return this.audioBotStartTime;
  };
  /**
 * @brief 获取本地或远程视频流截图并转换为Base64
 * @param streamIndex 视频流索引，默认为主流
 * @returns Promise<string | null> 返回Base64编码的图像字符串或null
 */
  getSnapshotAsBase64 = async (streamIndex: StreamIndex = StreamIndex.STREAM_INDEX_MAIN, userId?: string): Promise<string | null> => {
    try {
      // 如果提供了userId，则获取远程截图，否则获取本地截图
      if (userId) {
        // 获取远程截图
        const remoteImageData = await this.takeRemoteSnapshot(userId, streamIndex);
        if (!remoteImageData) {
          return null;
        }
        return this.imageDataToBase64(remoteImageData);
      } else {
        // 首先尝试获取屏幕截图（如果正在屏幕共享）
        if (streamIndex === StreamIndex.STREAM_INDEX_MAIN) {
          // 检查是否有屏幕共享流
          const screenVideoTrack = this.engine.getLocalStreamTrack(StreamIndex.STREAM_INDEX_SCREEN, 'video');
          if (screenVideoTrack && screenVideoTrack.readyState === 'live') {
            try {
              // 获取屏幕共享截图
              const screenImageData = await this.takeLocalSnapshot(StreamIndex.STREAM_INDEX_SCREEN);
              if (screenImageData) {
                return this.imageDataToBase64(screenImageData);
              }
            } catch (screenError) {
              console.warn('屏幕截图失败，回退到摄像头截图:', screenError);
              // 如果屏幕共享截图失败，回退到摄像头截图
              const cameraImageData = await this.takeLocalSnapshot(StreamIndex.STREAM_INDEX_MAIN);
              if (cameraImageData) {
                return this.imageDataToBase64(cameraImageData);
              }
            }
          } else {
            // 获取摄像头截图
            const cameraImageData = await this.takeLocalSnapshot(StreamIndex.STREAM_INDEX_MAIN);
            if (cameraImageData) {
              return this.imageDataToBase64(cameraImageData);
            }
          }
        } else {
          // 获取指定流的截图
          const imageData = await this.takeLocalSnapshot(streamIndex);
          if (imageData) {
            return this.imageDataToBase64(imageData);
          }
        }
      }
    } catch (error) {
      console.error('截图失败:', error);
      return null;
    }

    return null;
  };
}

export default new RTCClient();
